/* eslint-disable max-len */
import React from 'react';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Link } from 'components/core/Link';
import { H1, H4 } from 'components/core/Headline';
import { BackToTop } from 'components/core/BackToTop';
import './legalPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

const enhance = withPageViewTracking(PAGE_TYPES.LEGAL);

export function LegalReviewsPage() {
	return (
		<div className="legal-page">
				<div className="section-content">
					<H1 size="large">
						SpareFoot Review Terms and Conditions
					</H1>
					<p>
						These terms and conditions (the "Review Terms and Conditions") establish the terms that apply to facility reviews that you submit through SpareFoot (the "Website").
						<br /><br />
						By submitting a review of a self-storage facility to SpareFoot, Inc. ("SpareFoot") or responding to any review on the Website, or by submitting a review via [email, an online survey, comment card] or otherwise (each a "review"), you agree to these SpareFoot Review Terms and Conditions.
						<br /><br />
						The term "you" refers to the user submitting a review.
					</p>

					<H4 size="small">
						Review Guidelines
					</H4>
					<p>
						We encourage you to be upfront and honest in your self-storage facility review. To ensure reviews on the Website are useful and constructive, SpareFoot has a few guidelines to help you write an effective review.
					</p>

					<p>
						Your review should be:
					</p>

					<ul>
						<li>
							<p>
								<strong>Honest</strong>&mdash; Your review should be honest and not misleading. SpareFoot only posts reviews submitted by customers who have had a firsthand experience with the facility being reviewed. We will not accept reviews from anyone that has a relationship with the facility, including the owner, management and employees of the facility or a competing facility. We will not accept reviews from reviewers that receive compensation or the promise of compensation (such as a discount off the facility's rental fees or a free product) in exchange for a review. Failure to disclose any compensation you received or will receive in connection with a review is a violation of these Review Terms and Conditions and may be a violations of applicable law.
							</p>
						</li>
						<li>
							<p>
								<strong>Relevant</strong>&mdash; Reviews can be extremely helpful to fellow storage-seeking customers. Please stay on-topic in your review, and avoid discussing anything NOT related to the self-storage facility you are reviewing. Note that while we love hearing what you think of SpareFoot, your review should focus on the physical facility you rented from. To leave feedback about your experience with SpareFoot, please leave a review <Link href="https://www.trustpilot.com/review/www.sparefoot.com" >here</Link>.
							</p>
						</li>
						<li>
							<p>
								<strong>Appropriate</strong>&mdash;We know that not every customer experience is positive, so you're encouraged to share the good, the bad and the ugly. That being said, SpareFoot reserves the right to use our best judgement in publishing submitted reviews. To ensure your experience is ultimately shared with potential renters, please avoid the following in your review:
							</p>
							<ul>
								<li>content that is libelous, defamatory, obscene, pornographic, abusive or threatening</li>
								<li>profanity or hate speech</li>
								<li>promotion of illegal or immoral activities</li>
								<li>promotional content</li>
								<li>personal details including phone numbers, addresses, and full names of facility employees</li>
								<li>personal attacks on facility employees, including name-calling, rude comments, and defamation of character</li>
							</ul>
						</li>
					</ul>

					<p>
						Upon submission, you are required to provide a valid email address that SpareFoot may use to contact you in regard to your review. For more information on how SpareFoot may use your personal information, see our Privacy Policy.
					</p>
					<p>
						We ask that you provide your real name at the time of review submission, and refrain from misrepresenting yourself or others by submitting a review under a name other than your own. This includes the use of fictional characters and celebrity names. SpareFoot will not publish or display any name it believes to be a misrepresentation.
					</p>
					<p>
						SpareFoot reserves the right to monitor and publish all submitted reviews. If any review is found to be in violation of these Review Terms and Conditions, SpareFoot reserves the right to deny publication of the violating content. SpareFoot will cooperate fully with any law enforcement officials or agencies in the investigation of any violation of these Review Terms and Conditions or of any applicable laws. SpareFoot reserves the right to remove any content from the site at any time, for any reason.
					</p>

					<H4 size="small">
						Review Verification
					</H4>
					<p>
						To retain the integrity and quality of the review content on SpareFoot, submitted reviews are subject to email verification. In all cases, please allow up to 5 business days for your review to be approved and published.
					</p>
					<p>
						If you did not reserve storage directly through SpareFoot before leaving a review online, basic email verification is required&mdash; we send an email to the address provided with your review to ensure you're a real person who intended to leave the submitted feedback. Click the button in this email to verify, and you're all set. If you did reserve storage through SpareFoot before leaving a review online, no action is required.
					</p>

					<H4 size="small">
						License Grant
					</H4>
					<p>
						By submitting a review on the Website, you grant to SpareFoot and its affiliates a nonexclusive, royalty-free, perpetual, transferable, irrevocable and sublicensable right to use, reproduce, modify, adapt, translate, distribute, publish, create derivative works from and publicly display and perform such reviews throughout the world in any media, now known or hereafter devised.
					</p>

					<H4 size="small">
						Warranty Disclaimer
					</H4>
					<p>
						THE WEBSITE IS PROVIDED TO YOU STRICTLY ON AN "AS IS" BASIS. ALL CONDITIONS, REPRESENTATIONS AND WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF THIRD PARTY RIGHTS, ARE HEREBY DISCLAIMED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
					</p>

					<H4 size="small">
						Limitations of Liability
					</H4>
					<p>
						IN NO EVENT SHALL SPAREFOOT (AND/OR ITS OFFICERS, DIRECTORS, AGENTS, SUBSIDIARIES, JOINT VENTURES AND EMPLOYEES) BE LIABLE FOR ANY INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL OR CONSEQUENTIAL DAMAGES (INCLUDING BUT NOT LIMITED TO LOSS OF BUSINESS, REVENUE, PROFITS, USE, DATA OR OTHER ECONOMIC ADVANTAGE), HOWEVER SUCH DAMAGES ARISE, WHETHER IN ACTION OR INACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, OR ARISING OUT OF OR IN CONNECTION WITH THE USE OR INABILITY TO USE THE SERVICE OR MATERIALS AVAILABLE FROM THIS WEBSITE, EVEN IF SPAREFOOT HAS BEEN PREVIOUSLY ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. Some states do not allow the exclusion or limitation of incidental or consequential damages, so the above limitation or exclusion may not apply to you.
					</p>

					<H4 size="small">
						General
					</H4>
					<p>
						THIS AGREEMENT AND YOUR USE OF THE WEBSITE SHALL BE GOVERNED BY THE LAWS OF THE STATE OF TEXAS, WITHOUT REGARD TO PRINCIPLES OF CONFLICT OF LAWS.

					</p>
					<p>
						The state or federal courts sitting in Travis County, Texas shall have exclusive jurisdiction and venue over any dispute arising out of this agreement and sale, and you hereby consent to the jurisdiction of such courts. This Agreement may not be modified, supplemented, qualified, or interpreted by any trade usage or prior course of dealing not made a part of the Agreement by its express terms. The invalidity or unenforceability of any provision of this Agreement shall not affect the validity of this Agreement as a whole and any such provision should be enforced by authorities, and reconstructed if need be, to apply to the maximum extent allowable under applicable law. The failure by either party to enforce at any time any of the provisions of this Agreement, to exercise any election or option provided herein, or to require at any time the performance of the other of any of the provisions herein will not in any way be construed as a waiver of such provisions. Reasonable attorneys' fees and costs will be awarded to the prevailing party in the event of litigation involving the enforcement or interpretation of this Agreement. The section headings used herein are for convenience only and shall not be given any legal import.
					</p>
					<BackToTop />
				</div>
		</div>
	);
}

export default enhance(LegalReviewsPage);
